import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function AIHospitalityRevolutionPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'AI Hospitality Revolution: 5 Transformations' }
  ];

  const article = {
    category: 'Industry Trends & Technology Integration',
    date: 'August 14, 2025',
    title: 'AI Hospitality Revolution: 5 Transformations',
    subtitle: 'The Future of Butler Service Opened by Artificial Intelligence',
    heroImage: '/images/generated/jba-fs-046-news_ai-hospitality-revolution.webp',
    lead: "Hospitality Net expert Kate Fairweather analyzes five revolutionary transformations AI technology brings to the hospitality industry, based on insights gained through interactions with the Head Butler at London's Savoy Hotel.\n\nThis notable discourse comprehensively envisions the industry's future, from the complementary relationship between human butler service and AI technology to AI detox trends.",
    sections: [
      {
        heading: 'Augmented Emotional Intelligence',
        image: '/images/generated/jba-fs-047-news_ai-hospitality-revolution-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'AI capturing emotional cues to augment the intuition of human staff',
        paragraphs: [
          "AI technology analyzes thousands of guest interactions to understand patterns of emotional rhythms, stress indicators, and implicit needs, providing data-driven insights to human staff.",
          "This enables butlers to more accurately anticipate guests' deeper needs.",
          'AI concierges like Hilton\'s "Connie" go beyond simple information provision, learning from every interaction to understand what guests truly seek.',
          'In the future, "turbocharged human intuition" will be realized, detecting stress levels from voice patterns and identifying preferences from micro-expressions.'
        ]
      },
      {
        heading: 'Responsive Environment Realization',
        image: '/images/generated/jba-fs-048-news_ai-hospitality-revolution-v4.webp', aspect: 'aspect-[4/3]',
        alt: 'A guest room adjusting light and temperature to individual rhythms',
        paragraphs: [
          'The fusion of AI insights and smart devices transforms standardized static hospitality spaces into dynamic, responsive environments.',
          'A future is realized where guest rooms adjust lighting according to individual circadian rhythms, regulate temperature based on sleep patterns, and provide customized background noise.',
          'When recognizing jet lag, lighting adjusts and relaxation techniques are suggested; when recognizing celebrations, the atmosphere is enhanced. Spaces truly understand context and adjust environments to optimally create desired outcomes.'
        ]
      },
      {
        heading: 'Smart Sustainability Leap',
        image: '/images/generated/jba-fs-049-news_ai-hospitality-revolution-v3.webp', aspect: 'aspect-video',
        alt: 'An AI energy management system optimizing each room by occupancy',
        paragraphs: [
          'Like Accor Hotels, initiatives have begun integrating AI-powered energy management systems across properties, monitoring real-time occupancy data to optimize lighting, heating, and cooling for each room.',
          'AI tools predict restaurant demand, significantly reducing food waste.',
          'In the future, room-by-room and hour-by-hour energy consumption optimization through predictive systems analyzing behavior, weather patterns, local events, and historical data will be realized, maintaining guest comfort while minimizing waste through real-time resource optimization.'
        ]
      },
      {
        heading: 'Near-Future Novelty Experiences',
        image: '/images/generated/jba-fs-050-news_ai-hospitality-revolution-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'An immersive space shaped by AI-enhanced sound and light',
        paragraphs: [
          'Like Radisson Hotel Group\'s AI-leveraged immersive "infinity room," innovative hotels are experimenting with creative AI applications.',
          'Technology has been developed using AI-enhanced sound and light, enabling event planners to visualize spaces in real-time.',
          "In the near future, AI concierges customizing restaurant reservations and travel plans, AI chefs adjusting meals to guests' exact preferences using local seasonal ingredients, and AI spa treatments meeting guests' beauty and wellness goals will be realized."
        ]
      },
      {
        heading: 'Rise of AI Detox Trends',
        image: '/images/generated/jba-fs-051-news_ai-hospitality-revolution-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'A slow, artisanal, human-centred hospitality retreat away from technology',
        paragraphs: [
          'In an AI future where everything is smart, predictive, and adaptive, a desire for chronically non-AI experiences may emerge.',
          'Just as interest returned to "dumb" phones when digital addiction increased, demand for AI detox retreats and dumb hotels will rise.',
          'Destinations selling "non-augmented" staff, and as AI surges, a revival of luxury, artisanal, imperfect, slow hospitality may be seen, with the value of human butler service expected to be re-evaluated.'
        ]
      }
    ],
    comment: 'Kate Fairweather\'s five transformation analysis is an insightful discourse that brilliantly demonstrates the complementary relationship between AI technology and human butler service, with which our association deeply resonates.\n\nThe concept of "augmented emotional intelligence" is particularly interesting as a technological evolution of the "anticipatory spirit" we emphasize.\n\nThe AI detox trend observation shows that as technology advances, the value of warm human service increases, reaffirming the essential value of the butler profession.\n\nThe fusion of Japanese "omotenashi" culture and AI technology holds the potential to create a new hospitality model we can be proud of globally, and we will actively work toward establishing next-generation butler service that harmonizes traditional human capabilities with technology.',
    source: [
      { label: 'Source', value: 'Hospitality Net (by Kate Fairweather)', link: false },
      { label: 'Article URL', value: 'https://www.hospitalitynet.org/opinion/4128541.html', link: true },
      { label: 'Access Date', value: 'October 2025', link: false }
    ],
    related: [
      { to: '/en/news/apple-robot-butler', image: '/images/generated/jba-fs-052-news_ai-hospitality-revolution-v3.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Apple Robot Butler Development Project', title: 'Apple Robot Butler Development Project', desc: 'The future of butler service opened by AI technology' },
      { to: '/en/news/raffles-sentosa-butler-package', image: '/images/generated/seq-raffles-sentosa-card-1aa72f.webp', aspect: 'aspect-[3/4]', alt: 'Related article illustration of a luxury pool villa surrounded by tropical greenery', title: 'Raffles Sentosa Singapore "The Butler Did It" Package', desc: 'The value of butler service in ultra-premium packages' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: 'Source', value: 'Hospitality Net (by Kate Fairweather)', link: false },
    { label: 'Original Article', value: 'https://www.hospitalitynet.org/opinion/4128541.html', link: true },
    { label: 'Market Size Data', value: 'https://www.kingsresearch.com/ai-in-hospitality-market-671', link: true },
    { label: 'Adoption Statistics', value: 'https://www.escoffier.edu/blog/world-food-drink/hospitality-industry-trends-and-statistics/', link: true },
    { label: 'Connie (IBM)', value: 'https://uk.newsroom.ibm.com/2016-Mar-09-Hilton-and-IBM-Pilot-Connie-The-Worlds-First-Watson-Enabled-Hotel-Concierge', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];
  const toc = [
    { id: 's-emotional', label: 'Augmented Emotional Intelligence' },
    { id: 's-responsive', label: 'Responsive Environment Realization' },
    { id: 's-sustainability', label: 'Smart Sustainability Leap' },
    { id: 's-market', label: 'AI Hospitality Market Data' },
    { id: 's-novelty', label: 'Near-Future Novelty Experiences' },
    { id: 's-detox', label: 'Rise of AI Detox Trends' },
    { id: 's-connie', label: 'Case Study: Hilton\'s "Connie"' }
  ];

  return (
    <Layout>
      <title>AI Hospitality Revolution: 5 Transformations | Japan Butler Association</title>
      <meta name="description" content="Expert Kate Fairweather analyzes five revolutionary transformations AI technology brings to the hospitality industry, exploring the complementary relationship between human butler service and technology." />
      <link rel="canonical" href="https://butler-association.jp/en/news/ai-hospitality-revolution" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/ai-hospitality-revolution" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/ai-hospitality-revolution" />

      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <div className="space-y-4">
              {article.lead.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).map((s, i) => (
                <p key={i} className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{s}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'Hospitality Net expert Kate Fairweather analyzes five transformations AI brings to the hospitality industry',
                'Augmented emotional intelligence, responsive environments and smart sustainability place the AI–human complementary relationship at the core',
                'As AI spreads, a swing back to "non-augmented" human service (AI detox) re-evaluates the value of butler service'
              ]}
            />

            <SectionHeading id="s-emotional" en="Emotional Intelligence">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="As technology advances, the value of warm, human butler service only grows." cite="Kate Fairweather / Hospitality Net" />
            </div>

            <SectionHeading id="s-responsive" en="Responsive Space">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-sustainability" en="Sustainability">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-market" en="Market Data">AI Hospitality Market Data</SectionHeading>
            <div className="space-y-6">
              <p className={P}>According to research firm Kings Research, the AI-in-hospitality market reached about <Stat>$16.3B</Stat> in 2023 and is projected to grow at a 20.36% CAGR through 2031 to roughly <Stat>$70.3B</Stat>—a figure showing how rapidly investment is flowing into AI as the technological backbone of human hospitality.</p>
              <div className="space-y-5">
                <p className={P}>Adoption is already real: industry statistics compiled by culinary school Escoffier report that <Stat>89%</Stat> of hotels use AI for customer service and <Stat>64%</Stat> for housekeeping scheduling.</p>
                <p className={P}>AI is being built into day-to-day operations, freeing human staff for more creative and interpersonal roles.</p>
              </div>
            </div>
            <SourceShot
              src="/images/news-sources/aihosp-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page describing the hospitality industry"
              media="Wikipedia (Hospitality industry)"
              url="https://en.wikipedia.org/wiki/Hospitality_industry"
            />

            <SectionHeading id="s-novelty" en="Novelty">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-detox" en="AI Detox">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-connie" en="Case Study">Case Study: Hilton's Robot Concierge "Connie"</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>Hilton's "Connie," referenced in this article, was the world's first Watson-enabled robot concierge, piloted in March 2016 at Hilton McLean in Virginia.</p>
                <p className={P}>It answered guests by voice on hotel amenities, local attractions and dining recommendations, learning from every interaction.</p>
              </div>
              <p className={P}>The Connie case made concrete—as early as 2016—this article's central argument: that AI does not replace the human concierge or butler, but takes on routine queries so human staff can focus on higher-touch, interpersonal service.</p>
            </div>
            <SourceShot
              src="/images/news-sources/aihosp-ibm-connie.jpg"
              alt="Screenshot of the IBM official newsroom announcing the Hilton and IBM pilot of robot concierge Connie"
              media="IBM Newsroom"
              url="https://uk.newsroom.ibm.com/2016-Mar-09-Hilton-and-IBM-Pilot-Connie-The-Worlds-First-Watson-Enabled-Hotel-Concierge"
            />
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Publication</dt><dd className="text-[#333] mt-0.5">Hospitality Net</dd></div>
                  <div><dt className="text-[#8f7a56]">Author</dt><dd className="text-[#333] mt-0.5">Kate Fairweather</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                  <div><dt className="text-[#8f7a56]">Market (2031 est.)</dt><dd className="text-[#333] mt-0.5">approx. $70.3B</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
